import { useQuery } from '@tanstack/react-query';
import { listBillingReconciliationVariances } from '../api/billingReconciliationApi.js';

const DEFAULT_VARIANCE_PAGE_LIMIT = 500;
const MAX_VARIANCE_PAGES = 20;

const withMeta = (rows, meta) => {
  Object.defineProperty(rows, 'meta', {
    value: meta,
    enumerable: false,
    configurable: true,
  });
  return rows;
};

const normalizedPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.floor(parsed));
};

export const fetchBillingReconciliationVariancePages = async (params = {}, { signal } = {}) => {
  const limit = normalizedPositiveInt(params.limit, DEFAULT_VARIANCE_PAGE_LIMIT);
  const startOffset = normalizedPositiveInt(Number(params.offset) + 1, 1) - 1;
  let offset = startOffset;
  let total = 0;
  let hasMore = false;
  let pagesLoaded = 0;
  const rows = [];

  do {
    const request = { ...params, limit, offset };
    if (signal) request.signal = signal;
    const payload = await listBillingReconciliationVariances(request);
    const pageRows = Array.isArray(payload) ? payload : Array.isArray(payload?.rows) ? payload.rows : [];
    rows.push(...pageRows);
    pagesLoaded += 1;
    total = Number.isFinite(Number(payload?.total)) ? Number(payload.total) : rows.length;
    hasMore = Boolean(payload?.hasMore) && pageRows.length > 0 && pagesLoaded < MAX_VARIANCE_PAGES;
    offset += pageRows.length || limit;
  } while (hasMore);

  return withMeta(rows, {
    total,
    returned: rows.length,
    limit,
    offset: startOffset,
    pagesLoaded,
    hasMore: total > rows.length,
  });
};

export const useBillingReconciliationVariancePages = (params = {}, options = {}) => {
  const queryOptions = { ...options };
  delete queryOptions.retry;
  delete queryOptions.retryDelay;
  return useQuery({
    queryKey: ['billingRecon', 'variancesAll', params.runId || null, params.externalContextMode || null, params.driver || null, params.reviewed ?? null, params.search || null, params.limit || null],
    queryFn: ({ signal }) => fetchBillingReconciliationVariancePages(params, { signal }),
    enabled: (queryOptions.enabled ?? true) && Boolean(params.runId),
    staleTime: 60 * 1000,
    ...queryOptions,
    retry: false,
  });
};
